/* eslint-disable react/no-children-prop */
import {
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';

export interface TosModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const TOS_CONTENT = [
  {
    title: 'TERMS OF SERVICE',
    body: '',
    isHeading: true,
  },
  {
    title: '1. About these terms',
    body: 'These Terms of Service ("Terms") govern your use of Vital Educators ("Platform", "we", "us", or "our"), operated by Vital Educators Ltd, a company registered in England and Wales.\n\nBy creating an account, accessing, or using the Platform, you agree to these Terms in full. If you do not agree, you must not use the Platform.',
  },
  {
    title: '2. The service',
    body: 'Vital Educators is an online directory and communications platform connecting students and independent tutors.\n\nWe provide a searchable directory, on-platform messaging, optional tutor verification, and a student-release system.\n\nWe do NOT employ tutors, provide tutoring services, supervise lessons, guarantee outcomes, process lesson payments, or act as agent for either party.',
  },
  {
    title: '3. Your account',
    body: 'You must be at least 18 years old to create an account. Users under 18 may only use the Platform through a parent or legal guardian.\n\nYou agree to provide accurate information, maintain control of your email account, and use only one account unless authorised.',
  },
  {
    title: '4. Student release fee & premium subscription',
    body: 'Students are charged a one-time fee to access a tutor’s contact details. The standard fee is \u00A318.99 per tutor; tutors with an active Premium subscription offer a discounted rate of \u00A34.99.\n\nTutors may optionally purchase a Premium Tutor Subscription at \u00A339.99/month for enhanced visibility and benefits. Subscriptions renew automatically until cancelled.',
  },
  {
    title: '5. Refunds',
    body: 'Release fee refunds may be provided where tutor details are invalid, the tutor fails to respond within 14 days, or the tutor misrepresented their profile. Requests must be submitted within 30 days.\n\nPremium subscription payments are generally non-refundable once a billing cycle has commenced.',
  },
  {
    title: '6. Students & parents',
    body: 'You are solely responsible for conducting your own due diligence before engaging a tutor. Tutors are independent third parties. We do not guarantee tutor suitability, safety, qualifications, identity, or outcomes.\n\nWhere tuition involves minors, parents and guardians remain responsible for safeguarding decisions.',
  },
  {
    title: '7. Tutors',
    body: 'Tutors use the Platform as independent contractors — not employees, workers, agents, or partners of Vital Educators.\n\nTutors are solely responsible for tax obligations, insurance, safeguarding compliance, accuracy of profile information, and their own contracts with students.',
  },
  {
    title: '8. Verification badges',
    body: 'Vital Educators does not conduct DBS, PVG, AccessNI, or safeguarding checks on tutors.\n\nWhere a tutor uploads evidence of an existing certificate, we may display a verification badge indicating only that a document was uploaded and appeared valid on visual inspection. The badge does not constitute a guarantee of safety or suitability.',
  },
  {
    title: '9. Messaging & content',
    body: 'Contact details are restricted until a student pays the applicable release fee (\u00A318.99 standard or \u00A34.99 for Premium tutors).\n\nUsers must not bypass Platform protections, share disguised contact details, or circumvent fees. All content must be lawful, accurate, and not defamatory or misleading.',
  },
  {
    title: '10. Acceptable use',
    body: 'You must not use the Platform unlawfully, harass or abuse others, misrepresent qualifications or identity, upload false documents, manipulate reviews, scrape data, create fake accounts, or circumvent fees.\n\nWe may suspend or terminate accounts for breaches of these Terms.',
  },
  {
    title: '11. Reviews & rankings',
    body: 'Reviews must reflect genuine experiences. We reserve the right to moderate, remove, or refuse reviews, and to adjust search rankings at our discretion. We do not guarantee any specific ranking or business outcomes.',
  },
  {
    title: '12. Liability',
    body: 'The Platform is provided on an "as available" basis. To the fullest extent permitted by law, Vital Educators excludes liability for indirect or consequential loss, disputes between users, or acts of tutors or students.\n\nOur total aggregate liability shall not exceed the total fees paid by you during the previous 12 months.',
  },
  {
    title: '13. Closing your account & service shutdown',
    body: 'You may close your account at any time. We may retain certain records where legally required.\n\nIf the Platform is discontinued or shut down, we will provide a minimum of 30 days\u2019 notice via email to the address associated with your account and/or via a prominent notice on the Platform. During this notice period, you will continue to have access to your account.\n\nYou may request a copy of your personal data at any time by emailing support@vitaleducators.com. In the event of a shutdown, we recommend requesting your data within the notice period. After the Platform closes, we will retain personal data only for as long as legally required and will securely delete it thereafter.',
  },
  {
    title: '14. Privacy & data',
    body: 'Our handling of personal data is governed by our Privacy Policy. We process data in accordance with the UK GDPR and the Data Protection Act 2018.',
  },
  {
    title: '15. Governing law & disputes',
    body: 'These Terms are governed by the laws of England and Wales. Any dispute shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
  },
  {
    title: '16. Contact & changes',
    body: 'Questions regarding these Terms should be sent to support@vitaleducators.com.\n\nThe latest version published on our Terms of Service page will apply from its effective date.',
  },
];

export default function TosModal({
  isOpen,
  onAccept,
  onDecline,
}: TosModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Allow a small tolerance (5px) so the user doesn't have to hit
    // the exact last pixel.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= 5) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleAccept = useCallback(() => {
    setHasScrolledToBottom(false);
    onAccept();
  }, [onAccept]);

  const handleDecline = useCallback(() => {
    setHasScrolledToBottom(false);
    onDecline();
  }, [onDecline]);

  const overlayBg = useColorModeValue(
    'rgba(0, 0, 0, 0.6)',
    'rgba(0, 0, 0, 0.8)'
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDecline}
      size="2xl"
      closeOnOverlayClick={false}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg={overlayBg} />
      <ModalContent mx="1em" maxH="90vh" display="flex" flexDir="column">
        <ModalHeader fontSize="1.1em" pb="0.5em">
          Terms of Service
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody flex="1" overflow="hidden" px="0" pb="0">
          <Flex
            ref={scrollRef}
            onScroll={handleScroll}
            overflowY="auto"
            flexDir="column"
            gap="1.2em"
            px="1.5em"
            py="0.5em"
            maxH="60vh"
          >
            <Text fontSize="0.9em" color="gray.600" fontStyle="italic">
              Last updated: 18 May 2026
            </Text>

            <Text fontSize="0.9em" lineHeight="1.6">
              Welcome to Vital Educators. Please read the following Terms of
              Service carefully before creating your account. By clicking
              &quot;Accept&quot;, you confirm that you have read, understood,
              and agree to be bound by these terms. You can read the full Terms
              of Service here at any time.
            </Text>

            <Text fontSize="0.85em">
              <Link
                href="/terms-of-service"
                color="accent.500"
                isExternal
                fontWeight="500"
              >
                Read full Terms of Service →
              </Link>
            </Text>

            {TOS_CONTENT.map((section, index) => (
              <VStack
                key={`${section.title}-${index}`}
                align="stretch"
                spacing="0.4em"
              >
                <Text
                  fontWeight={section.isHeading ? '800' : '600'}
                  fontSize={section.isHeading ? '1.05em' : '0.95em'}
                  color={section.isHeading ? 'accent.500' : 'inherit'}
                  pt={section.isHeading ? '0.8em' : '0'}
                  pb={section.isHeading ? '0.2em' : '0'}
                  borderBottom={section.isHeading ? '2px solid' : 'none'}
                  borderColor={section.isHeading ? 'accent.200' : 'transparent'}
                >
                  {section.title}
                </Text>
                {section.body && (
                  <Text
                    fontSize="0.85em"
                    lineHeight="1.65"
                    color="gray.700"
                    whiteSpace="pre-line"
                  >
                    {section.body}
                  </Text>
                )}
              </VStack>
            ))}

            {/* Invisible sentinel that guarantees there's always something
                to scroll past so the scroll-to-unlock mechanism works even
                on very tall viewports. */}
            <Flex h="1px" />

            {!hasScrolledToBottom && (
              <Flex
                position="sticky"
                bottom="0"
                left="0"
                right="0"
                py="1em"
                px="0.5em"
                bg="linear-gradient(transparent, white 40%)"
                pointerEvents="none"
                justify="center"
              >
                <Text
                  fontSize="0.8em"
                  color="gray.500"
                  textAlign="center"
                  bg="whiteAlpha.900"
                  px="1em"
                  py="0.3em"
                  borderRadius="full"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  ↓ Scroll down to enable the accept button
                </Text>
              </Flex>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter
          pt="1em"
          pb="1.2em"
          px="1.5em"
          borderTop="1px solid"
          borderColor="gray.100"
          gap="0.8em"
          flexDir="column"
          alignItems="stretch"
        >
          {!hasScrolledToBottom && (
            <Text fontSize="0.75em" color="gray.400" textAlign="center">
              Please scroll through the entire Terms of Service to continue.
            </Text>
          )}
          <Flex justify="flex-end" gap="0.8em">
            <Button variant="outline" onClick={handleDecline} minW="6em">
              Decline
            </Button>
            <Button
              colorScheme="accent"
              onClick={handleAccept}
              isDisabled={!hasScrolledToBottom}
              minW="6em"
            >
              Accept
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
